import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";
import { ConfigProvider, Layout, Menu, Spin } from "antd";
import {
  ApartmentOutlined,
  DashboardOutlined,
  FundOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import actions from "./redux/admin/actions";
import withRouter from "./hoc/withRouter";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import UsersPage from "./pages/UsersPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import OrganizationDetailsPage from "./pages/OrganizationDetailsPage";
import IntelligenceCostsPage from "./pages/IntelligenceCostsPage";
import Topbar from "./components/topbar";
import Wrapper from "./components/adminShell/index.style";
import { getAdminTheme, getStoredAdminThemeMode, adminShellMetrics } from "./theme";
import { applyAtlasTheme, normalizeThemeMode, storeThemeMode } from "../theme";

const { Sider, Content, Footer } = Layout;
const { checkAuth } = actions;

export class AdminApp extends Component {
  constructor(props) {
    super(props);

    const themeMode = getStoredAdminThemeMode();
    applyAtlasTheme(themeMode);

    this.state = { themeMode };
  }

  componentDidMount() {
    this.props.checkAuth();
  }

  handleThemeChange = (themeMode) => {
    const normalizedThemeMode = normalizeThemeMode(themeMode);

    storeThemeMode(normalizedThemeMode);
    applyAtlasTheme(normalizedThemeMode);
    this.setState({ themeMode: normalizedThemeMode });
  };

  render() {
    const { operator, authChecking, t, router } = this.props;
    const { themeMode } = this.state;
    const { location, navigate } = router;
    const selectedMenuKey = location.pathname.startsWith("/admin/organisations/")
      ? "/admin/organizations"
      : location.pathname;
    const menuItems = [
      { key: "/admin", icon: <DashboardOutlined />, label: t("admin.nav.dashboard") },
      { key: "/admin/users", icon: <TeamOutlined />, label: t("admin.nav.users") },
      { key: "/admin/organizations", icon: <ApartmentOutlined />, label: t("admin.nav.organizations") },
      { key: "/admin/intelligence/costs", icon: <FundOutlined />, label: t("admin.nav.intelligenceCosts") },
    ];

    if (authChecking) {
      return (
        <Wrapper>
          <div className="admin-loading"><Spin size="large" /></div>
        </Wrapper>
      );
    }

    if (!operator) {
      return (
        <ConfigProvider theme={getAdminTheme(themeMode)}>
          <Routes>
            <Route path="login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/admin/login" replace />} />
          </Routes>
        </ConfigProvider>
      );
    }

    return (
      <ConfigProvider theme={getAdminTheme(themeMode)}>
        <Wrapper>
          <Layout className="admin-layout">
            <Topbar themeMode={themeMode} onThemeChange={this.handleThemeChange} />
            <Layout className="admin-body-layout">
              <Sider
                className="admin-sider"
                width={adminShellMetrics.siderWidth}
                breakpoint="lg"
                collapsible
              >
                <Menu
                  mode="inline"
                  selectedKeys={[selectedMenuKey]}
                  items={menuItems}
                  onClick={({ key }) => navigate(key)}
                />
              </Sider>
              <Layout>
                <Content className="admin-content">
                  <Routes>
                    <Route index element={<DashboardPage />} />
                    <Route path="users" element={<UsersPage />} />
                    <Route path="organizations" element={<OrganizationsPage />} />
                    <Route path="organisations/:organizationSlug" element={<OrganizationDetailsPage />} />
                    <Route path="organizations/:organizationSlug" element={<OrganizationDetailsPage />} />
                    <Route path="intelligence/costs" element={<IntelligenceCostsPage />} />
                    <Route path="login" element={<Navigate to="/admin" replace />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Routes>
                </Content>
                <Footer className="admin-footer">
                  {t("admin.layout.footer", { year: new Date().getFullYear() })}
                </Footer>
              </Layout>
            </Layout>
          </Layout>
        </Wrapper>
      </ConfigProvider>
    );
  }
}

AdminApp.propTypes = {
  operator: PropTypes.shape({ email: PropTypes.string.isRequired }),
  authChecking: PropTypes.bool.isRequired,
  checkAuth: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({ pathname: PropTypes.string.isRequired }).isRequired,
    navigate: PropTypes.func.isRequired,
  }).isRequired,
};

AdminApp.defaultProps = {
  operator: null,
};

const mapStateToProps = ({ Admin }) => ({
  operator: Admin.operator,
  authChecking: Admin.authChecking,
});

const mapDispatchToProps = (dispatch) => ({
  checkAuth: () => dispatch(checkAuth()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("common")(withRouter(AdminApp)));
